import React from 'react';
import { Shield } from 'lucide-react';
import { Card, CardContent } from '../../../ui/card';
import { Badge } from '../../../ui/badge';

interface TrustMetrics {
  totalFieldsCompared: number;
  matchingFields: number;
  resolvedFields: number;
  missingFields: number;
  lastUpdated: string;
}

interface FieldTrust {
  field: string;
  unifiedValue: string | number;
  status: 'Active' | 'Missing';
}

interface DataSummaryTabProps {
  metrics: TrustMetrics;
  fieldTrust: FieldTrust[];
}

export const DataSummaryTab: React.FC<DataSummaryTabProps> = ({ metrics, fieldTrust }) => {
  const getStatusVariant = (status: string): 'default' | 'destructive' => {
    return status === 'Active' ? 'default' : 'destructive';
  };


  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="size-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Data Summary</h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-sm text-muted-foreground mb-1">Total Fields</div>
              <div className="text-2xl font-semibold">{metrics.totalFieldsCompared}</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-sm text-green-700 mb-1">Matching Fields</div>
              <div className="text-2xl font-semibold text-green-700">{metrics.matchingFields}</div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-sm text-blue-700 mb-1">Resolved Fields</div>
              <div className="text-2xl font-semibold text-blue-700">{metrics.resolvedFields}</div>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="text-sm text-red-700 mb-1">Missing Fields</div>
              <div className="text-2xl font-semibold text-red-700">{metrics.missingFields}</div>
            </div>
          </div>

          {/* <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-sm text-muted-foreground mb-1">Last Updated</div>
            <div className="text-sm font-medium">{formatDateWithMonthName(metrics.lastUpdated)}</div>
          </div> */}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <h3 className="text-lg font-semibold mb-6">Field Status</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-0 text-sm font-medium text-gray-600">Field</th>
                  <th className="text-left py-4 px-0 text-sm font-medium text-gray-600">Unified Value</th>
                  <th className="text-right py-4 px-0 text-sm font-medium text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {fieldTrust.map((field, index) => (
                  <tr key={index} className="border-b border-gray-100 last:border-b-0">
                    <td className="py-4 px-0  text-gray-900 text-sm">{field.field}</td>
                    <td className="py-4 px-0 text-gray-700 text-sm">{field.unifiedValue}</td>
                    <td className="py-4 px-0 text-right">
                      <Badge
                        variant={getStatusVariant(field.status)}
                        className={field.status === 'Active' ? 'rounded-full bg-green-50 text-green-700 border border-green-200' : 'rounded-full bg-red-50 text-red-700 border border-red-200'}
                      >
                        {field.status}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
